import { z } from 'zod';
import { ToolEffect } from './guardrails.js';
import { Locator } from './recording.js';

/**
 * Flows: an executable procedure (usually synthesized from a recording) that an agent runs. Steps
 * are tried in cost order — an MCP tool call with templated arguments runs without any model;
 * `llm` (one structured call on the cheapest model), `agent` (a child run on the agent's runtime)
 * and `browser` (headless replay, last resort) only where no deterministic step exists.
 *
 * Templates: any string inside `args` / `input` / `value` / `output` may contain `{{params.x}}` or
 * `{{steps.<id>.output.<path>}}`. A string that is exactly one template keeps the referenced
 * value's type; otherwise values are stringified into the text. There are no expressions.
 */

export const FLOW_STEP_KINDS = ['mcp_call', 'llm', 'ask_human', 'agent', 'browser'] as const;
export type FlowStepKind = (typeof FLOW_STEP_KINDS)[number];
/** Step kinds the flow runner executes today (the others are synthesized as blockers). */
export const RUNNABLE_STEP_KINDS: readonly FlowStepKind[] = ['mcp_call', 'llm', 'ask_human'];

const Ident = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters, digits and _ (starting with a letter)');

export const FlowParam = z.object({
  name: Ident,
  type: z.enum(['string', 'number', 'boolean', 'enum', 'date']).default('string'),
  required: z.boolean().default(true),
  default: z.unknown().optional(),
  options: z.array(z.string().max(200)).max(50).optional(),
  description: z.string().max(300).default(''),
});
export type FlowParam = z.infer<typeof FlowParam>;

export const FlowCondition = z.object({
  ref: z.string().min(1).max(200),
  op: z.enum(['eq', 'neq', 'exists', 'truthy']),
  value: z.unknown().optional(),
});
export type FlowCondition = z.infer<typeof FlowCondition>;

const stepBase = {
  id: Ident,
  title: z.string().min(1).max(200),
  when: FlowCondition.optional(),
  onError: z.enum(['fail', 'skip']).default('fail'),
  /** Recording events this step was synthesized from (provenance, shown in the editor). */
  source: z.object({ eventSeqs: z.array(z.number().int()).max(500) }).optional(),
};

export const BrowserAction = z.object({
  op: z.enum(['goto', 'click', 'fill', 'select', 'press', 'wait_for', 'extract']),
  target: Locator.optional(),
  value: z.unknown().optional(),
});
export type BrowserAction = z.infer<typeof BrowserAction>;

export const FlowStep = z.discriminatedUnion('kind', [
  z.object({
    ...stepBase,
    kind: z.literal('mcp_call'),
    server: z.object({
      /** The org's server (resolved at synthesis; the runner looks the tool up via the binding prefix). */
      mcpServerId: z.string().max(64).optional(),
      presetId: z.string().max(64).optional(),
      prefix: z
        .string()
        .min(1)
        .max(24)
        .regex(/^[a-z0-9_]+$/),
    }),
    tool: z.string().min(1).max(200),
    args: z.record(z.string(), z.unknown()).default({}),
    /** Effect at synthesis time (display only: the policy decides at run time). */
    effect: ToolEffect.default('write'),
  }),
  z.object({
    ...stepBase,
    kind: z.literal('llm'),
    mode: z.enum(['extract', 'decide', 'transform']),
    prompt: z.string().min(1).max(500),
    input: z.unknown(),
    /** decide: the answer is one of these. */
    options: z.array(z.string().max(100)).max(20).optional(),
    /** extract/transform: JSON Schema of the answer (object). */
    schema: z.record(z.string(), z.unknown()).optional(),
    /** `provider:model`; default: the cheapest enabled model. */
    model: z.string().max(200).optional(),
  }),
  z.object({
    ...stepBase,
    kind: z.literal('ask_human'),
    question: z.string().min(1).max(1000),
    options: z.array(z.string().max(200)).max(10).optional(),
  }),
  z.object({
    ...stepBase,
    kind: z.literal('agent'),
    instruction: z.string().min(1).max(2000),
    input: z.unknown(),
    maxSteps: z.number().int().min(1).max(15).default(8),
  }),
  z.object({
    ...stepBase,
    kind: z.literal('browser'),
    startUrl: z.string().max(2000),
    actions: z.array(BrowserAction).max(100),
    /** Hosts the headless browser may reach (checked against the agent's egress allowlist). */
    egress: z.array(z.string().max(255)).max(20).default([]),
    fallback: z.enum(['agent', 'fail']).default('agent'),
    maxAgentSteps: z.number().int().min(1).max(20).default(10),
  }),
]);
export type FlowStep = z.infer<typeof FlowStep>;
export type FlowStepOf<K extends FlowStepKind> = Extract<FlowStep, { kind: K }>;

export const FLOW_MAX_STEPS = 50;
export const FlowSpec = z.object({
  v: z.literal(1).default(1),
  params: z.array(FlowParam).max(20).default([]),
  steps: z.array(FlowStep).min(1).max(FLOW_MAX_STEPS),
  /** Rendered into the run result (default: the last step's output). */
  output: z.unknown().optional(),
});
export type FlowSpec = z.infer<typeof FlowSpec>;

/** What must be in place before the flow can run end-to-end (computed at synthesis/save time). */
export const FlowPrereq = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('server_missing'), presetId: z.string(), name: z.string() }),
  z.object({ kind: z.literal('server_needs_auth'), mcpServerId: z.string(), name: z.string() }),
  z.object({
    kind: z.literal('write_access'),
    mcpServerId: z.string(),
    name: z.string(),
    tool: z.string(),
  }),
  z.object({ kind: z.literal('scope_missing'), presetId: z.string(), note: z.string() }),
  z.object({
    kind: z.literal('unsupported_step'),
    stepId: z.string(),
    stepKind: z.enum(FLOW_STEP_KINDS),
    reason: z.string(),
  }),
]);
export type FlowPrereq = z.infer<typeof FlowPrereq>;

export const FlowEstimate = z.object({
  mcpSteps: z.number().int(),
  llmSteps: z.number().int(),
  agentSteps: z.number().int(),
  browserSteps: z.number().int(),
  /** Model cost of one run (MCP steps cost nothing); null when no price is known. */
  estRunUsd: z.number().nullable(),
});
export type FlowEstimate = z.infer<typeof FlowEstimate>;

export const FlowSynthesisInfo = z.object({
  costUsd: z.number(),
  model: z.string().nullable(),
  llmCalls: z.number().int(),
  /** Segments that could not be mapped to an MCP tool. */
  unresolved: z.number().int(),
  durationMs: z.number().int(),
});
export type FlowSynthesisInfo = z.infer<typeof FlowSynthesisInfo>;

/** An agent version's flow attachment (pinVersion null = always the flow's current version). */
export const FlowBinding = z.object({
  flowId: z.string().uuid(),
  pinVersion: z.number().int().min(1).nullable().default(null),
});
export type FlowBinding = z.infer<typeof FlowBinding>;

export const FLOW_STATUSES = ['draft', 'active', 'archived'] as const;
export const FlowStatus = z.enum(FLOW_STATUSES);
export type FlowStatus = z.infer<typeof FlowStatus>;

export const FlowVersionDto = z.object({
  id: z.string(),
  version: z.number(),
  spec: FlowSpec,
  prerequisites: z.array(FlowPrereq),
  estimate: FlowEstimate,
  synthesis: FlowSynthesisInfo.nullable(),
  changelog: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type FlowVersionDto = z.infer<typeof FlowVersionDto>;

export const FlowDto = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  status: FlowStatus,
  currentVersion: z.number(),
  sourceRecordingId: z.string().nullable(),
  /** Agents whose current version binds this flow. */
  agentIds: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  latest: FlowVersionDto,
});
export type FlowDto = z.infer<typeof FlowDto>;

export const FlowInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(1000).default(''),
  spec: FlowSpec,
});
export const FlowPatch = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().max(1000).optional(),
  status: FlowStatus.optional(),
});
export const FlowVersionInput = z.object({
  spec: FlowSpec,
  changelog: z.string().max(500).optional(),
});
export const FlowRunInput = z.object({
  agentId: z.string().uuid(),
  params: z.record(z.string(), z.unknown()).default({}),
});
export const FlowAttachInput = z.object({
  agentId: z.string().uuid(),
  pinVersion: z.number().int().min(1).nullable().default(null),
});
export const FlowRunResult = z.object({ runId: z.string(), taskId: z.string() });
export type FlowRunResult = z.infer<typeof FlowRunResult>;
export const FlowAttachResult = z.object({
  agentId: z.string(),
  version: z.number(),
  /** MCP servers bound to the agent along with the flow. */
  addedServers: z.array(z.string()),
});
export type FlowAttachResult = z.infer<typeof FlowAttachResult>;

// ---------------------------------------------------------------------------------------------
// Jobs: the API asks for a synthesis through the outbox (transactional with the stop); the
// worker moves it onto its own queue.

export const FLOWS_QUEUE = 'af-flows';
export type FlowJob = { kind: 'synthesize'; orgId: string; recordingId: string };
/** Internal outbox event: synthesize this recording into a flow. */
export const FLOW_SYNTH_EVENT = 'recording.synthesize_requested';

// ---------------------------------------------------------------------------------------------
// Templates

export interface TemplateScope {
  params: Record<string, unknown>;
  steps: Record<string, { output: unknown }>;
}

const WHOLE = /^\{\{\s*([^{}]+?)\s*\}\}$/;
const PART = /\{\{\s*([^{}]+?)\s*\}\}/g;
const SEGMENT = /^([A-Za-z0-9_-]+)((?:\[\d+\])*)$/;

/** `params.x` / `steps.s1.output.items[0].id` → path segments, or null when malformed. */
export function parseRef(ref: string): (string | number)[] | null {
  const out: (string | number)[] = [];
  for (const part of ref.trim().split('.')) {
    const m = SEGMENT.exec(part);
    if (!m) return null;
    out.push(m[1] as string);
    for (const idx of (m[2] ?? '').matchAll(/\[(\d+)\]/g)) out.push(Number(idx[1]));
  }
  const root = out[0];
  if (root !== 'params' && root !== 'steps') return null;
  if (root === 'steps' && (out.length < 3 || out[2] !== 'output')) return null;
  return out;
}

/** Resolve a reference against the scope (own properties only); undefined when absent. */
export function resolveRef(ref: string, scope: TemplateScope): unknown {
  const path = parseRef(ref);
  if (!path) return undefined;
  let cur: unknown = scope;
  for (const key of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    if (typeof key === 'number') {
      if (!Array.isArray(cur)) return undefined;
      cur = cur[key];
    } else {
      if (!Object.hasOwn(cur, key)) return undefined;
      cur = (cur as Record<string, unknown>)[key];
    }
  }
  return cur;
}

const asText = (v: unknown): string =>
  v === undefined || v === null ? '' : typeof v === 'string' ? v : JSON.stringify(v);

/** Render every template inside a JSON value. */
export function renderTemplate(t: unknown, scope: TemplateScope): unknown {
  if (typeof t === 'string') {
    const whole = WHOLE.exec(t);
    if (whole) return resolveRef(whole[1] as string, scope);
    return t.replace(PART, (_, ref: string) => asText(resolveRef(ref, scope)));
  }
  if (Array.isArray(t)) return t.map((x) => renderTemplate(x, scope));
  if (t && typeof t === 'object')
    return Object.fromEntries(Object.entries(t).map(([k, v]) => [k, renderTemplate(v, scope)]));
  return t;
}

/** Every reference used inside a JSON value. */
export function templateRefs(t: unknown, out: string[] = []): string[] {
  if (typeof t === 'string') for (const m of t.matchAll(PART)) out.push((m[1] as string).trim());
  else if (Array.isArray(t)) for (const x of t) templateRefs(x, out);
  else if (t && typeof t === 'object') for (const v of Object.values(t)) templateRefs(v, out);
  return out;
}

export function evaluateCondition(c: FlowCondition, scope: TemplateScope): boolean {
  const v = resolveRef(c.ref, scope);
  switch (c.op) {
    case 'exists':
      return v !== undefined && v !== null;
    case 'truthy':
      return Boolean(v) && !(Array.isArray(v) && v.length === 0);
    case 'eq':
      return JSON.stringify(v) === JSON.stringify(c.value);
    case 'neq':
      return JSON.stringify(v) !== JSON.stringify(c.value);
  }
}

function stepTemplates(s: FlowStep): unknown[] {
  switch (s.kind) {
    case 'mcp_call':
      return [s.args];
    case 'llm':
    case 'agent':
      return [s.input];
    case 'browser':
      return [s.startUrl, ...s.actions.map((a) => a.value)];
    case 'ask_human':
      return [s.question];
  }
}

/**
 * Static checks shared by the API, the synthesizer and the editor: unique step ids, references
 * only to declared params and EARLIER steps, valid reference syntax. Returns human-readable errors.
 */
export function validateFlowSpec(spec: FlowSpec): string[] {
  const errors: string[] = [];
  const params = new Set<string>();
  for (const p of spec.params) {
    if (params.has(p.name)) errors.push(`Duplicate parameter "${p.name}"`);
    params.add(p.name);
    if (p.type === 'enum' && !p.options?.length) errors.push(`Parameter "${p.name}" needs options`);
  }
  const seen = new Set<string>();
  const check = (ref: string, where: string) => {
    const path = parseRef(ref);
    if (!path) return errors.push(`${where}: invalid reference {{${ref}}}`);
    if (path[0] === 'params' && !params.has(String(path[1])))
      errors.push(`${where}: unknown parameter "${String(path[1])}"`);
    if (path[0] === 'steps' && !seen.has(String(path[1])))
      errors.push(`${where}: step "${String(path[1])}" is not an earlier step`);
  };
  for (const s of spec.steps) {
    if (seen.has(s.id)) errors.push(`Duplicate step id "${s.id}"`);
    const where = `Step "${s.id}"`;
    if (s.when) check(s.when.ref, where);
    for (const t of stepTemplates(s)) for (const ref of templateRefs(t)) check(ref, where);
    seen.add(s.id);
  }
  for (const ref of templateRefs(spec.output)) check(ref, 'Output');
  return errors;
}

/** Highest effect of the flow's tool steps (drives the policy of the `run_flow` tool). */
export function flowEffect(spec: FlowSpec): z.infer<typeof ToolEffect> {
  const order = ToolEffect.options;
  let max = 0;
  for (const s of spec.steps) {
    const e = s.kind === 'mcp_call' ? s.effect : s.kind === 'browser' ? 'external' : 'read';
    max = Math.max(max, order.indexOf(e));
  }
  return order[max] ?? 'read';
}

/** Validate run parameters against the declared params (defaults applied). */
export function resolveFlowParams(
  spec: FlowSpec,
  given: Record<string, unknown>,
): { ok: true; params: Record<string, unknown> } | { ok: false; error: string } {
  const out: Record<string, unknown> = {};
  for (const p of spec.params) {
    const v = Object.hasOwn(given, p.name) ? given[p.name] : p.default;
    if (v === undefined || v === null || v === '') {
      if (p.required) return { ok: false, error: `Missing parameter "${p.name}"` };
      continue;
    }
    if (p.type === 'number' && typeof v !== 'number' && Number.isNaN(Number(v)))
      return { ok: false, error: `Parameter "${p.name}" must be a number` };
    if (p.type === 'enum' && !p.options?.includes(String(v)))
      return { ok: false, error: `Parameter "${p.name}" must be one of ${p.options?.join(', ')}` };
    out[p.name] = p.type === 'number' ? Number(v) : p.type === 'boolean' ? v === true || v === 'true' : v;
  }
  return { ok: true, params: out };
}
